import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

const ThemeContext = createContext(null);

// Theme modes
export const THEME_MODES = {
  GRADIENT: "gradient",
  FLAT: "flat"
};

// Default UnCloned theme colors - EXPANDED 8-COLOR SYSTEM
export const DEFAULT_THEME = {
  mode: THEME_MODES.GRADIENT, // gradient or flat
  // Core colors
  primary: "#df1283",      // Hot pink - buttons, accents
  secondary: "#FF7901",    // Orange - gradients, hover
  background: "#2D063E",   // Deep purple - main bg
  background_secondary: "#4A0A66", // Lighter purple - cards
  text: "#FFFFFF",         // White text for readability
  text_secondary: "#E0E0E0", // Light gray for secondary
  // Additional colors for depth
  border: "#df1283",       // Border color
  accent: "#0FFFE2",       // Cyan - highlights, badges
  input_background: "#4A0A66", // Input field backgrounds
  // Logo colors
  logo_primary: "#df1283", // Logo gradient start
  logo_secondary: "#FF7901", // Logo gradient end
};

// Emoji skin tone modifiers
export const SKIN_TONES = [
  { id: "default", name: "Default", modifier: "", sample: "👋" },
  { id: "light", name: "Light", modifier: "\u{1F3FB}", sample: "👋🏻" },
  { id: "medium_light", name: "Medium-Light", modifier: "\u{1F3FC}", sample: "👋🏼" },
  { id: "medium", name: "Medium", modifier: "\u{1F3FD}", sample: "👋🏽" },
  { id: "medium_dark", name: "Medium-Dark", modifier: "\u{1F3FE}", sample: "👋🏾" },
  { id: "dark", name: "Dark", modifier: "\u{1F3FF}", sample: "👋🏿" },
];

// Base emojis that support skin tones (hand-based emojis)
const SKIN_TONE_EMOJIS = [
  "👋", "🤚", "🖐️", "✋", "🖖", "👌", "🤌", "🤏", "✌️", "🤞", "🤟", "🤘", "🤙",
  "👈", "👉", "👆", "🖕", "👇", "☝️", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏",
  "🙌", "👐", "🤲", "🤝", "🙏", "✍️", "💅", "🤳", "💪", "🦵", "🦶", "👂", "🦻",
  "👃", "👶", "👧", "🧒", "👦", "👩", "🧑", "👨", "👩‍🦱", "🧑‍🦱", "👨‍🦱", "👩‍🦰",
  "🧑‍🦰", "👨‍🦰", "👱‍♀️", "👱", "👱‍♂️", "👩‍🦳", "🧑‍🦳", "👨‍🦳", "👩‍🦲", "🧑‍🦲",
  "👨‍🦲", "🧔‍♀️", "🧔", "🧔‍♂️", "👵", "🧓", "👴", "👲", "👳‍♀️", "👳", "👳‍♂️",
  "🧕", "👮‍♀️", "👮", "👮‍♂️", "👷‍♀️", "👷", "👷‍♂️", "💂‍♀️", "💂", "💂‍♂️",
  "🕵️‍♀️", "🕵️", "🕵️‍♂️", "👩‍⚕️", "🧑‍⚕️", "👨‍⚕️", "👩‍🌾", "🧑‍🌾", "👨‍🌾",
  "👩‍🍳", "🧑‍🍳", "👨‍🍳", "👩‍🎓", "🧑‍🎓", "👨‍🎓", "👩‍🎤", "🧑‍🎤", "👨‍🎤",
  "👩‍🏫", "🧑‍🏫", "👨‍🏫", "👩‍🏭", "🧑‍🏭", "👨‍🏭", "👩‍💻", "🧑‍💻", "👨‍💻",
  "👩‍💼", "🧑‍💼", "👨‍💼", "👩‍🔧", "🧑‍🔧", "👨‍🔧", "👩‍🔬", "🧑‍🔬", "👨‍🔬",
  "👩‍🎨", "🧑‍🎨", "👨‍🎨", "👩‍🚒", "🧑‍🚒", "👨‍🚒", "👩‍✈️", "🧑‍✈️", "👨‍✈️",
  "👩‍🚀", "🧑‍🚀", "👨‍🚀", "👩‍⚖️", "🧑‍⚖️", "👨‍⚖️", "👰‍♀️", "👰", "👰‍♂️",
  "🤵‍♀️", "🤵", "🤵‍♂️", "👸", "🤴", "🥷", "🦸‍♀️", "🦸", "🦸‍♂️", "🦹‍♀️",
  "🦹", "🦹‍♂️", "🤶", "🧑‍🎄", "🎅", "🧙‍♀️", "🧙", "🧙‍♂️", "🧝‍♀️", "🧝",
  "🧝‍♂️", "🧛‍♀️", "🧛", "🧛‍♂️", "🧜‍♀️", "🧜", "🧜‍♂️", "🧚‍♀️", "🧚", "🧚‍♂️",
  "👼", "🤰", "🫃", "🫄", "🤱", "👩‍🍼", "🧑‍🍼", "👨‍🍼", "🙇‍♀️", "🙇", "🙇‍♂️",
  "💁‍♀️", "💁", "💁‍♂️", "🙅‍♀️", "🙅", "🙅‍♂️", "🙆‍♀️", "🙆", "🙆‍♂️", "🙋‍♀️",
  "🙋", "🙋‍♂️", "🧏‍♀️", "🧏", "🧏‍♂️", "🤦‍♀️", "🤦", "🤦‍♂️", "🤷‍♀️", "🤷",
  "🤷‍♂️", "🙎‍♀️", "🙎", "🙎‍♂️", "🙍‍♀️", "🙍", "🙍‍♂️", "💇‍♀️", "💇", "💇‍♂️",
  "💆‍♀️", "💆", "💆‍♂️", "🧖‍♀️", "🧖", "🧖‍♂️", "💃", "🕺", "🕴️", "👯‍♀️", "👯",
  "👯‍♂️", "🧑‍🤝‍🧑", "👭", "👫", "👬", "🏃‍♀️", "🏃", "🏃‍♂️", "🚶‍♀️", "🚶",
  "🚶‍♂️", "🧍‍♀️", "🧍", "🧍‍♂️", "🧎‍♀️", "🧎", "🧎‍♂️"
];

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Helper to convert hex to RGB values
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Apply theme colors as CSS variables
// Batch all DOM updates using requestAnimationFrame to prevent layout thrashing
const applyThemeToDOM = (theme) => {
  requestAnimationFrame(() => {
    const root = document.documentElement;
    
    // Primary color
    root.style.setProperty('--theme-primary', theme.primary);
    const primaryRgb = hexToRgb(theme.primary);
    if (primaryRgb) {
      root.style.setProperty('--theme-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }
  
  // Secondary color
  root.style.setProperty('--theme-secondary', theme.secondary);
  const secondaryRgb = hexToRgb(theme.secondary);
  if (secondaryRgb) {
    root.style.setProperty('--theme-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  }
  
  // Background colors
  root.style.setProperty('--theme-background', theme.background);
  root.style.setProperty('--theme-background-secondary', theme.background_secondary);
  
  const bgRgb = hexToRgb(theme.background);
  if (bgRgb) {
    root.style.setProperty('--theme-background-rgb', `${bgRgb.r}, ${bgRgb.g}, ${bgRgb.b}`);
  }
  
  const bgSecRgb = hexToRgb(theme.background_secondary);
  if (bgSecRgb) {
    root.style.setProperty('--theme-background-secondary-rgb', `${bgSecRgb.r}, ${bgSecRgb.g}, ${bgSecRgb.b}`);
  }
  
  // Text colors
  root.style.setProperty('--theme-text', theme.text || DEFAULT_THEME.text);
  root.style.setProperty('--theme-text-secondary', theme.text_secondary || DEFAULT_THEME.text_secondary);
  
  const textRgb = hexToRgb(theme.text || DEFAULT_THEME.text);
  if (textRgb) {
    root.style.setProperty('--theme-text-rgb', `${textRgb.r}, ${textRgb.g}, ${textRgb.b}`);
  }
  
  // Border color
  const borderColor = theme.border || theme.primary;
  root.style.setProperty('--theme-border', borderColor);
  const borderRgb = hexToRgb(borderColor);
  if (borderRgb) {
    const borderRgbStr = `${borderRgb.r}, ${borderRgb.g}, ${borderRgb.b}`;
    root.style.setProperty('--theme-border-rgb', borderRgbStr);
  } else {
    const primaryRgbFallback = hexToRgb(theme.primary);
    if (primaryRgbFallback) {
      root.style.setProperty('--theme-border-rgb', `${primaryRgbFallback.r}, ${primaryRgbFallback.g}, ${primaryRgbFallback.b}`);
    }
  }
  
  // Accent color (for highlights, badges)
  const accentColor = theme.accent || theme.secondary || theme.primary;
  root.style.setProperty('--theme-accent', accentColor);
  const accentRgb = hexToRgb(accentColor);
  if (accentRgb) {
    root.style.setProperty('--theme-accent-rgb', `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`);
  }
  
  // Button text color - white for dark buttons, dark for light buttons
  // Calculate luminance to determine if we need dark or light text
  // Reuse primaryRgb from above
  if (primaryRgb) {
    const luminance = (0.299 * primaryRgb.r + 0.587 * primaryRgb.g + 0.114 * primaryRgb.b) / 255;
    const buttonTextColor = luminance > 0.5 ? '#1a1a2e' : '#ffffff';
    root.style.setProperty('--theme-button-text', buttonTextColor);
  } else {
    root.style.setProperty('--theme-button-text', '#ffffff');
  }
  
  // Input background color
  const inputBg = theme.input_background || theme.background_secondary;
  root.style.setProperty('--theme-input-background', inputBg);
  const inputBgRgb = hexToRgb(inputBg);
  if (inputBgRgb) {
    root.style.setProperty('--theme-input-background-rgb', `${inputBgRgb.r}, ${inputBgRgb.g}, ${inputBgRgb.b}`);
  }
  
  // Logo colors (for gradient)
  root.style.setProperty('--theme-logo-primary', theme.logo_primary || theme.primary);
  root.style.setProperty('--theme-logo-secondary', theme.logo_secondary || theme.secondary);
  
  // Helper to convert hex to HSL for Shadcn compatibility
  const hexToHSL = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };
  
  // Update Shadcn HSL variables for Tailwind compatibility
  root.style.setProperty('--primary', hexToHSL(theme.primary));
  root.style.setProperty('--secondary', hexToHSL(theme.secondary));
  root.style.setProperty('--accent', hexToHSL(theme.primary));
  root.style.setProperty('--background', hexToHSL(theme.background));
  
  // Theme mode - gradient or flat
  const mode = theme.mode || THEME_MODES.GRADIENT;
  root.style.setProperty('--theme-mode', mode);
  
  // Set gradient vs flat-specific variables
  if (mode === THEME_MODES.FLAT) {
    // Flat mode - use solid colors, NO transparency
    root.style.setProperty('--theme-button-bg', theme.primary);
    root.style.setProperty('--theme-button-hover', theme.secondary);
    root.style.setProperty('--theme-page-bg', theme.background);
    root.style.setProperty('--theme-gradient-text', theme.primary);
    // Solid background for glass cards in flat mode - no transparency
    root.style.setProperty('--glass-card-bg', theme.background_secondary);
    root.style.setProperty('--glass-backdrop', 'none');
  } else {
    // Gradient mode - use gradients with subtle transparency
    root.style.setProperty('--theme-button-bg', `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`);
    root.style.setProperty('--theme-button-hover', `linear-gradient(90deg, ${theme.secondary}, ${theme.primary})`);
    root.style.setProperty('--theme-page-bg', `linear-gradient(135deg, ${theme.background} 0%, ${theme.background_secondary} 50%, ${theme.background} 100%)`);
    root.style.setProperty('--theme-gradient-text', `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`);
    // Glass effect with transparency in gradient mode
    root.style.setProperty('--glass-card-bg', `rgba(${bgSecRgb.r}, ${bgSecRgb.g}, ${bgSecRgb.b}, 0.7)`);
    root.style.setProperty('--glass-backdrop', 'blur(24px)');
  }
  }); // End requestAnimationFrame
};

// Apply skin tone to an emoji
export const applySkinTone = (emoji, skinToneId) => {
  if (!skinToneId || skinToneId === "default") return emoji;
  
  const skinTone = SKIN_TONES.find(st => st.id === skinToneId);
  if (!skinTone || !skinTone.modifier) return emoji;
  
  // Check if this emoji supports skin tones
  const baseEmoji = emoji.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
  
  // Only apply to emojis that EXACTLY match one in our skin tone list
  // Don't use includes() as it causes false positives (e.g., 🎨 matching 👩‍🎨)
  if (SKIN_TONE_EMOJIS.includes(baseEmoji)) {
    // Remove any existing skin tone and add the new one
    return baseEmoji + skinTone.modifier;
  }
  
  return emoji;
};

export const ThemeProvider = ({ children, userTheme, userSkinTone, isB2BPortal = false }) => {
  // Store the original B2B client theme for reset functionality
  const [originalB2BTheme, setOriginalB2BTheme] = useState(null);
  
  const [theme, setTheme] = useState(() => {
    // DON'T load from localStorage on initial render - let the actual userTheme take precedence
    // This prevents showing stale cached theme when switching between portals
    if (userTheme) {
      return { ...DEFAULT_THEME, ...userTheme };
    }
    return DEFAULT_THEME;
  });
  
  // Track if we're in preview mode to prevent userTheme from overriding
  const [isPreviewLocked, setIsPreviewLocked] = useState(false);
  
  const [skinTone, setSkinTone] = useState(() => {
    try {
      return localStorage.getItem('uncloned_skin_tone') || userSkinTone || "default";
    } catch (e) {
      console.warn('Failed to load skin tone:', e);
      return "default";
    }
  });

  // Store the original B2B theme when it first loads
  useEffect(() => {
    if (isB2BPortal && userTheme && !originalB2BTheme) {
      setOriginalB2BTheme({ ...DEFAULT_THEME, ...userTheme, _isB2B: true });
    }
  }, [isB2BPortal, userTheme, originalB2BTheme]);

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyThemeToDOM(theme);
    // Only save non-B2B themes to localStorage to prevent B2B theme leakage
    if (!theme._isB2B) {
      try {
        localStorage.setItem('uncloned_theme', JSON.stringify(theme));
      } catch (e) {
        console.error('Failed to save theme to localStorage:', e);
      }
    }
  }, [theme]);

  // Update theme when user theme changes OR when B2B portal status changes
  useEffect(() => {
    // Skip if in preview mode - preview theme should be locked
    if (isPreviewLocked) {
      return;
    }
    
    // When on B2B portal with a theme, apply it immediately
    if (isB2BPortal && userTheme && userTheme._isB2B) {
      setTheme({ ...DEFAULT_THEME, ...userTheme });
      applyThemeToDOM({ ...DEFAULT_THEME, ...userTheme });
      return;
    }
    
    // If NOT on B2B portal, reset to default UnCloned theme
    // This ensures leaving a B2B portal shows correct branding
    if (!isB2BPortal) {
      // Use user's personal theme if available, otherwise default
      const newTheme = userTheme ? { ...DEFAULT_THEME, ...userTheme } : DEFAULT_THEME;
      setTheme(newTheme);
      applyThemeToDOM(newTheme);
      return;
    }
    
    if (userTheme) {
      // For B2B users with full theme, replace entirely; for partial themes, merge
      const hasFullTheme = userTheme.background && userTheme.primary && userTheme.secondary;
      if (hasFullTheme) {
        // Full B2B theme - replace everything
        setTheme({ ...DEFAULT_THEME, ...userTheme });
      } else {
        // Partial personal customizations - merge
        setTheme(prev => ({ ...prev, ...userTheme }));
      }
    }
  }, [userTheme, isPreviewLocked, isB2BPortal]);
  
  // Update skin tone when user skin tone changes
  useEffect(() => {
    if (userSkinTone) {
      setSkinTone(userSkinTone);
      try {
        localStorage.setItem('uncloned_skin_tone', userSkinTone);
      } catch (e) {
        console.error('Failed to save skin tone to localStorage:', e);
      }
    }
  }, [userSkinTone]);

  const updateTheme = useCallback((newTheme) => {
    // If this is a B2B theme update, apply directly to DOM to ensure it sticks
    if (newTheme._isB2B) {
      const mergedTheme = { ...DEFAULT_THEME, ...newTheme };
      setTheme(mergedTheme);
      applyThemeToDOM(mergedTheme);
    } else {
      setTheme(prev => ({ ...prev, ...newTheme }));
    }
  }, []);

  // Lock preview mode to prevent userTheme from overriding
  const lockPreviewTheme = useCallback(() => {
    setIsPreviewLocked(true);
  }, []);

  // Unlock preview mode (when navigating away from preview)
  const unlockPreviewTheme = useCallback(() => {
    setIsPreviewLocked(false);
  }, []);

  const resetTheme = useCallback(() => {
    // For B2B users, reset to their client's brand colors, not the UnCloned default
    if (originalB2BTheme) {
      setTheme(originalB2BTheme);
    } else {
      setTheme(DEFAULT_THEME);
      localStorage.removeItem('uncloned_theme');
    }
    setIsPreviewLocked(false);
  }, [originalB2BTheme]);
  
  const updateSkinTone = useCallback((newSkinTone) => {
    setSkinTone(newSkinTone);
    localStorage.setItem('uncloned_skin_tone', newSkinTone);
  }, []);
  
  // Helper function to apply skin tone to emoji
  const getEmoji = useCallback((emoji) => applySkinTone(emoji, skinTone), [skinTone]);
  
  // Check if current theme is gradient mode
  const isGradientMode = theme.mode === THEME_MODES.GRADIENT;

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ 
    theme, 
    updateTheme, 
    resetTheme, 
    lockPreviewTheme,
    unlockPreviewTheme,
    isPreviewLocked,
    DEFAULT_THEME,
    skinTone,
    updateSkinTone,
    getEmoji,
    SKIN_TONES,
    isGradientMode,
    THEME_MODES
  }), [theme, skinTone, isGradientMode, isPreviewLocked, updateTheme, resetTheme, lockPreviewTheme, unlockPreviewTheme, updateSkinTone, getEmoji]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
